import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../../../../axiosConfig';
import Loading from '../../../../components/globales/Loading';
import TablaLayout from '../../../../components/globales/TablaLayout';
import ModalDynamic from '../../../../components/globales/ModalDynamic';
import useCrud from '../../../../hooks/useCrud';
import ENDPOINTS from '../../../../api/endpoints';
import { respuestaFormSchema } from '../../../../services/validators';

export default function Respuestas() {
    const { id } = useParams();
    const keyField = 'id';

    const respuestasEndpoints = useMemo(() => ({
        listar: ENDPOINTS.encuestasRespuestas.listar(id),
        crear: ENDPOINTS.encuestasRespuestas.crear,
        actualizar: ENDPOINTS.encuestasRespuestas.actualizar,
        borrar: ENDPOINTS.encuestasRespuestas.borrar
    }), [id]);

    const initForm = () => ({
        nombre: '',
        verdadera: '',
        id_tipo_dato: '',
        aplica_observacion: '',
        pregunta: id
    });

    const [tipoDatos, setTipoDatos] = useState([]);
    const [parentEncuestaId, setParentEncuestaId] = useState(null);

    // Opciones para campos Sí/No - CORREGIDO
    const opcionesSiNo = useMemo(() => [
        { value: '1', label: 'Sí' },
        { value: '0', label: 'No' }
    ], []);

    useEffect(() => {
        axios.get(respuestasEndpoints.listar)
            .then(res => {
                // Mapear datoRespuesta del servidor a tipoDatos
                const lista = res.data.datoRespuesta || [];
                setTipoDatos(
                    lista.map(d => ({
                        value: String(d.id),
                        label: d.nombre
                    }))
                );
                // Obtener el ID de la encuesta padre
                setParentEncuestaId(res.data.pregunta?.id_encuesta || null);
            })
            .catch(error => {
                console.error('Error al cargar datos:', error);
            });
    }, [respuestasEndpoints]);

    // Columnas para la tabla
    const columns = [
        { label: 'No', key: 'index', align: 'center', render: (_, i) => i + 1 },
        { label: 'Nombre', key: 'nombre', width: '30%' },
        { 
            label: 'Verdadero (opcional)', 
            key: 'verdadera', 
            align: 'center', 
            render: (_, idx, item) => {
                if (item.verdadera === 1 || item.verdadera === true || item.verdadera === '1') return 'Sí';
                if (item.verdadera === 0 || item.verdadera === false || item.verdadera === '0') return 'No';
                return '-';
            }
        },
        { 
            label: 'Tipo Dato', 
            key: 'id_tipo_dato', 
            options: tipoDatos, 
            width: '20%',
            render: (_, idx, item) => {
                const tipoId = String(item.id_tipo_dato || '');
                const tipoFound = tipoDatos.find(o => o.value === tipoId);
                return tipoFound ? tipoFound.label : '-';
            }
        },
        { 
            label: 'Aplica Observación', 
            key: 'aplica_observacion', 
            align: 'center', 
            render: (_, idx, item) => {
                if (item.aplica_observacion === 1 || item.aplica_observacion === true || item.aplica_observacion === '1') return 'Sí';
                if (item.aplica_observacion === 0 || item.aplica_observacion === false || item.aplica_observacion === '0') return 'No';
                return '-';
            }
        }
    ];

    // Columnas para el formulario 
    const formColumns = useMemo(() => [
        { label: 'Nombre', key: 'nombre', type: 'text', required: true, width: '30%' },
        { label: 'Verdadero (opcional)', key: 'verdadera', type: 'select', options: opcionesSiNo, required: false, align: 'center' },
        { label: 'Tipo Dato', key: 'id_tipo_dato', type: 'select', options: [...tipoDatos], required: true, width: '20%' },
        { label: 'Aplica Observación (opcional)', key: 'aplica_observacion', type: 'select', options: opcionesSiNo, required: false, align: 'center' }
    ], [opcionesSiNo, tipoDatos]);

    const breadcrumbs = [
        { label: 'Inicio', href: '/inicio' },
        { label: 'Encuestas', href: '/encuestas' },
        { label: 'Preguntas', href: `/preguntas/${parentEncuestaId}` },
        { label: 'Respuestas' }
    ];

    // Función para transformar datos antes de enviar
    const transformRequest = (formData) => {
        const transformedData = {
            ...formData,
            verdadera: formData.verdadera && formData.verdadera !== '' ? parseInt(formData.verdadera, 10) : null,
            datoRespuesta: parseInt(formData.id_tipo_dato, 10), // Cambiar id_tipo_dato por datoRespuesta
            aplica_observacion: formData.aplica_observacion && formData.aplica_observacion !== '' ? parseInt(formData.aplica_observacion, 10) : null,
            pregunta: parseInt(formData.pregunta, 10)
        };
        
        // Eliminar id_tipo_dato del objeto final
        delete transformedData.id_tipo_dato;
        
        return transformedData;
    };

    const {
        data,
        loading,
        error,
        showModal,
        actionType,
        formData,
        setFormData,
        errors,
        openModal,
        closeModal,
        handleChange,
        submit,
        remove
    } = useCrud(respuestasEndpoints, { keyField, initForm, columns: formColumns, schema: respuestaFormSchema, transformRequest });

    // Función helper para normalizar valores booleanos
    const normalizeBooleanToString = (value) => {
        if (value === null || value === undefined || value === '') {
            return '';
        }
        
        if (typeof value === 'boolean') {
            return value ? '1' : '0';
        }
        
        if (typeof value === 'number') {
            return value === 1 ? '1' : '0';
        }
        
        if (typeof value === 'string') {
            if (value === 'true') return '1';
            if (value === 'false') return '0';
            if (['1', '0'].includes(value)) return value;
        }
        
        return '';
    };

    // Usar en el useEffect:
    useEffect(() => {
        if (showModal && actionType === 'editar' && formData) {
            let needsUpdate = false;
            const updates = {};

            // Mapear campos del servidor al formulario
            if (formData.datoRespuesta !== undefined && formData.id_tipo_dato === undefined) {
                updates.id_tipo_dato = String(formData.datoRespuesta);
                needsUpdate = true;
            }

            // Normalizar id_tipo_dato
            if (formData.id_tipo_dato !== undefined && typeof formData.id_tipo_dato !== 'string') {
                updates.id_tipo_dato = String(formData.id_tipo_dato);
                needsUpdate = true;
            }

            // Normalizar verdadera
            const normalizedVerdadera = normalizeBooleanToString(formData.verdadera);
            if (normalizedVerdadera !== formData.verdadera) {
                updates.verdadera = normalizedVerdadera;
                needsUpdate = true;
            }

            // Normalizar aplica_observacion
            const normalizedAplicaObservacion = normalizeBooleanToString(formData.aplica_observacion);
            if (normalizedAplicaObservacion !== formData.aplica_observacion) {
                updates.aplica_observacion = normalizedAplicaObservacion;
                needsUpdate = true;
            }

            // Asegurar pregunta
            if (!formData.pregunta) {
                updates.pregunta = id;
                needsUpdate = true;
            }

            if (needsUpdate) {
                setFormData(prev => ({ ...prev, ...updates }));
            }
        }
    }, [showModal, actionType, formData, setFormData, id]);

    if (loading) return <Loading mensaje="Cargando Respuestas..." />;
    if (error) return <div className="text-danger">Error: {error.message}</div>;

    return (
        <div className="container">
            <TablaLayout
                title="Respuestas"
                data={data}
                columns={columns}
                keyField={keyField}
                breadcrumbs={breadcrumbs}
                onCreate={() => openModal('crear')}
                onView={item => openModal('consultar', { ...item, pregunta: id })}
                onEdit={item => openModal('editar', { ...item, pregunta: id })}
                onDelete={remove}
                disableDelete={item => item.registros_encuestas > 0}
                showConsult={false}
            />

            <ModalDynamic
                show={showModal}
                accion={actionType}
                title="Respuesta"
                columns={formColumns}
                formData={formData}
                errors={errors}
                onChange={handleChange}
                onSubmit={submit}
                onClose={closeModal}
            />
        </div>
    );
}
